import * as React from "react";
import { Menubar } from "@bizform/primereact/components/menubar/Menubar";
import { MenubarProps } from "@bizform/primereact/menubar";
import { MenuItem } from "@bizform/primereact/components/menuitem/MenuItem";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { AccessibilityMenu, ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = MenubarProps & {
};

type WrappedCompState = {
    // attribute
    model: MenuItem[];
    style?: object;
    className?: string;
};

type State = {
    show: boolean;
    displayMode: number;
    wrappedCompState: WrappedCompState;
};

/**
 * @since 0.0.1
 * @author Jongkwang Park, Hwansoon Park
 * @class
 * @description
 * Menubar component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfMenubar extends AbstractComponentElement<Props, State> {

    /* manifest */
    /*
     * property panel 에서 이 manifest 를 기준으로 속성 및 이벤트를 설정할 때
     * Menubar 컴포넌트의 model 프로퍼티는 그 type 이 FORMSTATE 이므로
     * 메뉴바의 값 변경 시 emitValue 가 아닌 emitFormData 를 수행한다.
     * 또한, command 는 외부에서 재정의할 수 없으며(property panel에 노출시키지 않음)
     * property panel 에서 설정한 Form State 변경만 가능하게 사용자 인터페이스를 제공해야 한다.
     * 단, 각각의 서브 메뉴가 built-in command 를 호출하기 위해서
     * 적절한 별도의 property 정의가 필요하다.
     */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_I2hgP_yncSLzkxTE4uIyXS6kqr3FulE",
        category: ComponentCategory.MENU,
        label: I18n.componentElement.menuBar.label,
        description: I18n.componentElement.menuBar.description,
        icon: "",
        property: null
    };

    focus(): void {
        if (this._menuBar) {
            this._menuBar.focus();
        }
    }

    closeOverlay(): void {
        if (this.state.wrappedCompState && this.state.wrappedCompState.model) {
            this.setState({
                wrappedCompState: {
                    model: ObjectHelper.cloneDeep(this.state.wrappedCompState.model)
                }
            });
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this._newModel = true;
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    private _itemValueEvent(e: {originalEvent: Event, item: MenuItem}): void {
        /*
         * 주의:
         * itemValue 는 MenuItem 에 정의된 값이 프로퍼티가 아니다.
         * property panel 에서 form state 변경을 위해 itemValue 를 컴포넌트에 설정하고
         * command 함수(메뉴 클릭)가 호출될 때 이 itemValue 를 전달하기 위해
         * MenuItem 객체에 강제로 주입된 속성이다.
         */
        const itemValue = e.item && (e.item as {itemValue}).itemValue;
        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emitFormState(value: string): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfMenubar)
    static init(): ComponentElement<null> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _newModel: boolean;
    private _menuBar: HTMLDivElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                model: null,
            }
        };

        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        this._newModel = false;
    }

    componentWillUnmount(): void {
        this._menuBar = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            return this._render4Edit();
        }
    }

    private _render4Edit(): React.ReactNode {
        const w = this.state.wrappedCompState;
        if (this.accessibility) {
            return (
                <AccessibilityMenu
                    {...w}
                    id={this.elementId}
                    key={this.elementId}
                    newModel={this._newModel}
                    isHorizontal={true}
                    ref={this._setRef}
                />
            );
        } else {
            return (
                <Menubar
                    {...w}
                    key={this.elementId}
                    id={this.elementId}
                    ref={this._setRef}
                />
            );
        }
    }

    private _setRef(ref): void {
        if (ref) {
            this._menuBar = ref.element;
            this.readyComponent();
        }
    }
}
